/**
 * Providers and the router (frontend.md §1.1).
 *
 * React Router in declarative mode, every route element lazily loaded so the
 * table bundle — GSAP and the card atlas, from M3 — never reaches the auth
 * screens.
 */

import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';

import { refreshSession } from './net/rest';
import { AppShell } from './routes/AppShell';
import { AuthSkeleton } from './routes/AuthSkeleton';
import { AnonymousOnly, RequireAuth } from './routes/guards';
import { RootRedirect } from './routes/RootRedirect';
import { SocialProvider } from './routes/social/SocialProvider';
import { useStore } from './store';
import { ToastViewport } from './ui/Toast';

const Login = lazy(() => import('./routes/auth/Login'));
const Register = lazy(() => import('./routes/auth/Register'));
const Verify = lazy(() => import('./routes/auth/Verify'));
const ForgotPassword = lazy(() => import('./routes/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./routes/auth/ResetPassword'));
const Lobby = lazy(() => import('./routes/lobby/Lobby'));
const Profile = lazy(() => import('./routes/profile/Profile'));
const Friends = lazy(() => import('./routes/social/Friends'));
const NotFound = lazy(() => import('./routes/NotFound'));
// The table bundle carries the card atlas CSS and, from M4, GSAP. Lazy so none
// of it reaches the auth screens (frontend.md §1.1).
const Room = lazy(() => import('./routes/room/Room'));
const JoinByCode = lazy(() => import('./routes/room/JoinByCode'));
const MockTable = lazy(() => import('./routes/room/MockTable'));

/**
 * `/mock-table` in development, and on a production bundle only when
 * `__ORDAGO_NO_ANIM` is set — the same condition `debug.ts:72` uses to install
 * `window.__ordago`, and set by the e2e suite's init script before the first
 * navigation (tests/e2e/fixtures.ts).
 *
 * `layout-geometry.spec.ts` needs it. That spec measures every HUD element
 * against every other one at eight viewport sizes across six phases, and doing
 * that against a live table would mean driving four browsers through an órdago
 * and a reveal to reach two of the six — minutes of scripted play to check
 * something that is a pure function of the CSS and one `TableModel`. The fixture
 * makes it a few seconds and removes the server from the loop entirely.
 *
 * Read at module scope because the init script runs before any app code. It
 * exposes nothing: the fixture is four hardcoded hands in the client bundle,
 * the same exposure class as `window.__ordago`, which this project already
 * accepted behind this flag.
 */
const mockTableEnabled =
  import.meta.env.DEV || (typeof window !== 'undefined' && window.__ORDAGO_NO_ANIM === true);

/**
 * Exchanges the httpOnly refresh cookie for an in-memory access token, once, on
 * boot. Until it answers the store stays `hydrating` and no guard redirects.
 */
function useSessionHydration(): void {
  useEffect(() => {
    let cancelled = false;
    void refreshSession().then((ok) => {
      if (cancelled || ok) return;
      useStore.getState().auth.markAnonymous();
    });
    return () => {
      cancelled = true;
    };
  }, []);
}

export function App() {
  useSessionHydration();

  return (
    <BrowserRouter>
      <Suspense fallback={<AuthSkeleton />}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />

          <Route
            path="/login"
            element={
              <AnonymousOnly>
                <Login />
              </AnonymousOnly>
            }
          />
          <Route
            path="/register"
            element={
              <AnonymousOnly>
                <Register />
              </AnonymousOnly>
            }
          />
          {/* Verify and reset are reachable while signed in: both are arrived at
              from an email link, and bouncing that to the lobby would strand the
              reader. */}
          <Route path="/verify" element={<Verify />} />
          <Route
            path="/forgot-password"
            element={
              <AnonymousOnly>
                <ForgotPassword />
              </AnonymousOnly>
            }
          />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route path="/lobby" element={<Lobby />} />
            {/* A modal route over the lobby: opening the create dialog must not
                unmount the list or drop the LobbyRoom connection. */}
            <Route path="/lobby/new" element={<Lobby />} />
            {/* Two paths, one resolver: `/j/:code` is what `shareLinkFor`
                builds and what gets pasted into a group chat, `/join/:code`
                is the readable form frontend.md §1.1 lists. */}
            <Route path="/j/:code" element={<JoinByCode />} />
            <Route path="/join/:code" element={<JoinByCode />} />
            <Route path="/profile" element={<Profile />} />
            {/* The same panel the shell's sheet holds. It is a real route
                because a right-side drawer is the wrong shape on a narrow
                screen, where it covers the page it should sit beside. */}
            <Route path="/friends" element={<Friends />} />
          </Route>

          {/* Outside the shell: the table owns the whole viewport, and the
              app chrome would steal the height the felt needs. */}
          <Route
            element={
              <RequireAuth>
                <Outlet />
              </RequireAuth>
            }
          >
            <Route path="/room/:roomId" element={<Room />} />
          </Route>

          {mockTableEnabled && <Route path="/mock-table" element={<MockTable />} />}

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      {/* Outside every route, because the table route lives outside the shell
          and an invitation has to reach a player who is already sitting down. */}
      <SocialProvider />

      {/* Mounted unconditionally: a live region added at the same moment as its
          first message is frequently not announced. */}
      <ToastViewport />
    </BrowserRouter>
  );
}
